/**
 * Hand-written validators for every inbound message.
 *
 * Deliberately dependency-free (see README).
 *
 * Written ES5-only, before the S0 probe showed goja supports far more (see
 * docs/goja-capabilities.md). It does not need modernising - plain loops and
 * charCode checks are fine here, and unnecessary churn in the one file that
 * stands between a hostile payload and the match loop is a bad trade.
 *
 * The contract every validator keeps: it NEVER throws. `matchLoop` runs shared
 * state, and an exception there can end the match for the whole room. A bad
 * payload must come back as a value, not as a stack unwind.
 */
import { ErrorCode } from './errors.js';
import { FORBIDDEN_KEYS, Limits } from './limits.js';
function fail(message) {
    return { ok: false, code: ErrorCode.MALFORMED, message: message };
}
function ok(value) {
    return { ok: true, value: value };
}
// --- primitives -------------------------------------------------------------
function isArray(value) {
    return Object.prototype.toString.call(value) === '[object Array]';
}
function isObject(value) {
    return typeof value === 'object' && value !== null && !isArray(value);
}
/** ES5 has no Number.isInteger. Rejects NaN, Infinity and fractions. */
function isInt(value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
}
function isNonNegativeInt(value) {
    return isInt(value) && value >= 0;
}
function isForbiddenKey(key) {
    const forbidden = FORBIDDEN_KEYS;
    for (let i = 0; i < forbidden.length; i++) {
        if (forbidden[i] === key)
            return true;
    }
    return false;
}
/**
 * Like `isName`, but hyphens are allowed: phrase ids read as words.
 */
function isPhraseId(value, maxLen = Limits.ACTION_NAME) {
    if (typeof value !== 'string' || value.length === 0 || value.length > maxLen)
        return false;
    for (let i = 0; i < value.length; i++) {
        const c = value.charCodeAt(i);
        const isDigit = c >= 48 && c <= 57;
        const isUpper = c >= 65 && c <= 90;
        const isLower = c >= 97 && c <= 122;
        const isSeparator = c === 95 || c === 45;
        if (!isDigit && !isUpper && !isLower && !isSeparator)
            return false;
    }
    return true;
}
/**
 * Identifier-shaped: letters, digits and underscore.
 *
 * A character loop rather than a regex - not because goja lacks regex, but
 * because this runs on attacker-controlled strings and a linear scan has no
 * pathological backtracking case.
 */
function isName(value, maxLen) {
    if (typeof value !== 'string' || value.length === 0 || value.length > maxLen)
        return false;
    for (let i = 0; i < value.length; i++) {
        const c = value.charCodeAt(i);
        const isDigit = c >= 48 && c <= 57;
        const isUpper = c >= 65 && c <= 90;
        const isLower = c >= 97 && c <= 122;
        const isUnderscore = c === 95;
        if (!isDigit && !isUpper && !isLower && !isUnderscore)
            return false;
    }
    return true;
}
/**
 * Depth-limited, size-limited JSON check.
 *
 * Returns a reason when the value is unacceptable, or null when it is fine.
 * Rejects the prototype keys outright rather than trusting every eventual
 * consumer to be careful with client-supplied lookup keys.
 */
function checkJson(value, depth) {
    if (depth > Limits.DEPTH)
        return 'nested too deeply';
    if (value === null)
        return null;
    const t = typeof value;
    if (t === 'boolean')
        return null;
    if (t === 'number')
        return isFinite(value) ? null : 'contains a non-finite number';
    if (t === 'string')
        return value.length > Limits.STRING ? 'contains an over-long string' : null;
    if (isArray(value)) {
        if (value.length > Limits.ARRAY)
            return 'contains an over-long array';
        for (let i = 0; i < value.length; i++) {
            const reason = checkJson(value[i], depth + 1);
            if (reason !== null)
                return reason;
        }
        return null;
    }
    if (isObject(value)) {
        const keys = Object.keys(value);
        if (keys.length > Limits.OBJECT_KEYS)
            return 'contains an object with too many keys';
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (isForbiddenKey(key))
                return 'contains a forbidden key: ' + key;
            if (key.length > Limits.STRING)
                return 'contains an over-long key';
            const reason = checkJson(value[key], depth + 1);
            if (reason !== null)
                return reason;
        }
        return null;
    }
    return 'contains an unsupported type: ' + t;
}
/** Rejects any key the payload should not carry, so typos surface immediately. */
function hasOnlyKeys(value, allowed) {
    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        let found = false;
        for (let j = 0; j < allowed.length; j++) {
            if (allowed[j] === key) {
                found = true;
                break;
            }
        }
        if (!found)
            return false;
    }
    return true;
}
// --- entry point ------------------------------------------------------------
/** JSON.parse with the try/catch in exactly one place. */
export function decode(raw) {
    if (typeof raw !== 'string')
        return fail('payload is not a string');
    try {
        return ok(JSON.parse(raw));
    }
    catch (_e) {
        return fail('payload is not valid JSON');
    }
}
// --- messages ---------------------------------------------------------------
const MOVE_KEYS = ['move', 'args', 'v'];
export function validateMove(payload) {
    if (!isObject(payload))
        return fail('MOVE must be an object');
    if (!hasOnlyKeys(payload, MOVE_KEYS))
        return fail('MOVE has unexpected keys');
    if (!isName(payload['move'], Limits.MOVE_NAME))
        return fail('MOVE.move is not a valid move name');
    if (!isNonNegativeInt(payload['v']))
        return fail('MOVE.v must be a non-negative integer');
    const args = payload['args'];
    if (!isArray(args))
        return fail('MOVE.args must be an array');
    if (args.length > Limits.MOVE_ARGS)
        return fail('MOVE.args has too many arguments');
    const reason = checkJson(args, 0);
    if (reason !== null)
        return fail('MOVE.args ' + reason);
    return ok({
        move: payload['move'],
        args: args,
        v: payload['v'],
    });
}
export function validateSyncRequest(payload) {
    if (!isObject(payload))
        return fail('SYNC_REQUEST must be an object');
    if (Object.keys(payload).length !== 0)
        return fail('SYNC_REQUEST takes no fields');
    return ok({});
}
/**
 * Tidy typed text into the thing that will actually be shown.
 *
 * Control characters out, whitespace runs to single spaces, ends trimmed.
 *
 * Exported because the **order matters and has to be the same everywhere**:
 * normalise, then measure. Measuring first would let a hundred newlines through
 * as a message inside the length limit that draws as a blank bubble, and a
 * client that tidied differently from the server would disagree about whether
 * its own message was too long.
 *
 * Deliberately not a sanitiser. Nothing here is rendered as markup — the client
 * puts it in a text node — so escaping would be a second, weaker defence in the
 * wrong place.
 */
export function normaliseChatText(value) {
    let out = '';
    for (let i = 0; i < value.length; i++) {
        const c = value.charCodeAt(i);
        // C0 and C7 control ranges, plus the line separators, become spaces.
        if (c < 32 || c === 127 || (c >= 0x80 && c <= 0x9f) || c === 0x2028 || c === 0x2029) {
            out += ' ';
            continue;
        }
        out += value.charAt(i);
    }
    return out.replace(/\s+/g, ' ').trim();
}
export function validateChat(payload) {
    if (!isObject(payload))
        return fail('CHAT must be an object');
    if (!hasOnlyKeys(payload, ['phraseId', 'text']))
        return fail('CHAT has unexpected keys');
    const hasPhrase = payload['phraseId'] !== undefined;
    const hasText = payload['text'] !== undefined;
    /*
     * Exactly one. Both would leave the server choosing which a player meant,
     * and neither is a message with nothing in it.
     */
    if (hasPhrase && hasText)
        return fail('CHAT carries both a phrase and text');
    if (!hasPhrase && !hasText)
        return fail('CHAT carries neither a phrase nor text');
    if (hasPhrase) {
        /*
         * Shape only. WHICH ids exist is the adapter's business, because only it
         * can see the configured list - the same division `SET_VARIANT` uses. What
         * this guarantees is that an id is short and identifier-shaped, so nothing
         * arriving here can be long, structured, or markup.
         */
        if (!isPhraseId(payload['phraseId']))
            return fail('CHAT.phraseId is not a valid id');
        return ok({ phraseId: payload['phraseId'] });
    }
    if (typeof payload['text'] !== 'string')
        return fail('CHAT.text is not a string');
    /*
     * A raw length check first, so a megabyte of spaces is refused before it is
     * walked character by character. The real limit is applied after normalising.
     */
    if (payload['text'].length > Limits.CHAT_TEXT * 8)
        return fail('CHAT.text is too long');
    const text = normaliseChatText(payload['text']);
    if (text.length === 0)
        return fail('CHAT.text is empty');
    if (text.length > Limits.CHAT_TEXT)
        return fail('CHAT.text is too long');
    return ok({ text });
}
const VOICE_KINDS = ['offer', 'answer', 'candidate', 'bye'];
/**
 * A signalling blob, checked for shape and size and nothing else.
 *
 * Deliberately **not** parsed. The server forwards the payload verbatim, so
 * this is the whole of what anybody checks: that it is addressed to somebody,
 * that the kind is one we relay, and that it is not enormous.
 *
 * Reading inside would make the server responsible for SDP — a format with its
 * own parsers, its own history of parser bugs, and no business being understood
 * by a card game. The recipient's browser is the thing that should reject a
 * malformed offer, and it already does.
 */
export function validateVoice(payload) {
    if (!isObject(payload))
        return fail('VOICE must be an object');
    if (!hasOnlyKeys(payload, ['to', 'kind', 'payload']))
        return fail('VOICE has unexpected keys');
    /*
     * The recipient is a Nakama user id, which is a UUID.
     *
     * Length-checked rather than pattern-matched: whether it names somebody in
     * this match is the server's question and only the server can answer it. What
     * this stops is an id long enough to be a payload of its own.
     */
    if (typeof payload['to'] !== 'string')
        return fail('VOICE.to is not a string');
    if (payload['to'].length === 0 || payload['to'].length > Limits.STRING) {
        return fail('VOICE.to is not a plausible user id');
    }
    if (typeof payload['kind'] !== 'string' || VOICE_KINDS.indexOf(payload['kind']) < 0) {
        return fail('VOICE.kind is not one we relay');
    }
    if (typeof payload['payload'] !== 'string')
        return fail('VOICE.payload is not a string');
    if (payload['payload'].length > Limits.VOICE_PAYLOAD)
        return fail('VOICE.payload is too long');
    /*
     * A goodbye carries nothing, and everything else has to carry something.
     *
     * An empty offer is a client bug that would otherwise be forwarded and fail
     * silently at the far end, which is a much harder thing to find.
     */
    if (payload['kind'] !== 'bye' && payload['payload'].length === 0) {
        return fail('VOICE.payload is empty');
    }
    return ok({
        to: payload['to'],
        kind: payload['kind'],
        payload: payload['payload'],
    });
}
/**
 * Three booleans, and nothing else.
 *
 * The whole state each time rather than a change to it: setting is idempotent
 * and order-independent, so two messages crossing cannot leave somebody muted
 * who is not, which a toggle could.
 */
export function validateVoiceState(payload) {
    if (!isObject(payload))
        return fail('VOICE_STATE must be an object');
    if (!hasOnlyKeys(payload, ['joined', 'micOff', 'earsOff'])) {
        return fail('VOICE_STATE has unexpected keys');
    }
    for (const key of ['joined', 'micOff', 'earsOff']) {
        if (typeof payload[key] !== 'boolean')
            return fail(`VOICE_STATE.${key} is not a boolean`);
    }
    return ok({
        joined: payload['joined'],
        micOff: payload['micOff'],
        earsOff: payload['earsOff'],
    });
}
/**
 * Which of two people makes the offer.
 *
 * Both notice each other at the same moment, and if both send an offer neither
 * connection forms — the classic glare. So the pair decides by comparing ids
 * rather than by racing: **the lower id offers, the higher answers.**
 *
 * Here rather than on the server because both ends need the same answer and
 * neither is asking permission — it is a rule about a pair, not a decision
 * about a request. Deterministic, needs no state, and works the same for a
 * player and an observer.
 */
export function shouldOffer(me, peer) {
    return me < peer;
}
/**
 * The face somebody has chosen for this table (D92).
 *
 * Shape only, exactly as a phrase id is: WHICH avatars exist, and which of
 * them this table offers, is the adapter's business because only it can see
 * the catalog and who is hosting. What this guarantees is that an id is short
 * and identifier-shaped, so nothing arriving here can be long, structured, or
 * markup.
 */
/**
 * "Look at what I am wearing again."
 *
 * Takes no fields, deliberately. A client that could name its own deck could
 * wear one it has not bought; this one can only ask the server to re-read the
 * row it wrote itself.
 */
export function validateLook(payload) {
    if (!isObject(payload))
        return fail('LOOK must be an object');
    if (Object.keys(payload).length !== 0)
        return fail('LOOK takes no fields');
    return ok({});
}
export function validateWear(payload) {
    if (!isObject(payload))
        return fail('WEAR must be an object');
    if (!hasOnlyKeys(payload, ['avatarId']))
        return fail('WEAR has unexpected keys');
    if (!isPhraseId(payload['avatarId']))
        return fail('WEAR.avatarId is not a valid id');
    return ok({ avatarId: payload['avatarId'] });
}
export function validateHostAction(payload) {
    if (!isObject(payload))
        return fail('HOST_ACTION must be an object');
    if (!isName(payload['action'], Limits.ACTION_NAME))
        return fail('HOST_ACTION.action is not a valid name');
    if (!isNonNegativeInt(payload['v']))
        return fail('HOST_ACTION.v must be a non-negative integer');
    const action = payload['action'];
    const v = payload['v'];
    if (action === 'START_ROUND' || action === 'PAUSE' || action === 'RESUME' || action === 'END_GAME') {
        if (!hasOnlyKeys(payload, ['action', 'v']))
            return fail('HOST_ACTION.' + action + ' takes no arguments');
        return ok({ action: action, v: v });
    }
    if (action === 'REMOVE_SEAT' ||
        action === 'SKIP_SEAT' ||
        action === 'ASSIGN_HOST' ||
        action === 'MAKE_OBSERVER') {
        if (!hasOnlyKeys(payload, ['action', 'seat', 'v']))
            return fail('HOST_ACTION.' + action + ' has unexpected keys');
        if (!isNonNegativeInt(payload['seat']))
            return fail('HOST_ACTION.seat must be a non-negative integer');
        return ok({ action: action, seat: payload['seat'], v: v });
    }
    if (action === 'SET_TURN_LIMIT') {
        if (!hasOnlyKeys(payload, ['action', 'turnLimitMs', 'v']))
            return fail('HOST_ACTION.SET_TURN_LIMIT has unexpected keys');
        // The permitted range lives in config/match.json and is enforced by the
        // adapter, which owns those bounds. This only guarantees a sane number
        // reaches it.
        if (!isInt(payload['turnLimitMs']) || payload['turnLimitMs'] <= 0) {
            return fail('HOST_ACTION.turnLimitMs must be a positive integer');
        }
        return ok({ action: action, turnLimitMs: payload['turnLimitMs'], v: v });
    }
    if (action === 'SET_AUTO_CONTINUE' ||
        action === 'SET_ALLOW_OBSERVERS' ||
        action === 'SET_ALLOW_TEXT_CHAT' ||
        action === 'SET_ALLOW_VOICE') {
        if (!hasOnlyKeys(payload, ['action', 'enabled', 'v']))
            return fail('HOST_ACTION.' + action + ' has unexpected keys');
        if (typeof payload['enabled'] !== 'boolean')
            return fail('HOST_ACTION.enabled must be a boolean');
        return ok({ action: action, enabled: payload['enabled'], v: v });
    }
    if (action === 'SET_VARIANT') {
        if (!hasOnlyKeys(payload, ['action', 'variantId', 'value', 'v']))
            return fail('HOST_ACTION.SET_VARIANT has unexpected keys');
        if (!isName(payload['variantId'], Limits.ACTION_NAME))
            return fail('HOST_ACTION.variantId is not a valid name');
        const value = payload['value'];
        // Only the three shapes a variant can be. Which one THIS variant accepts is
        // the adapter's business, since only it can see the game's declaration.
        const shaped = typeof value === 'boolean' ||
            typeof value === 'string' ||
            (typeof value === 'number' && isFinite(value));
        if (!shaped)
            return fail('HOST_ACTION.value must be a boolean, string or number');
        if (typeof value === 'string' && value.length > Limits.STRING)
            return fail('HOST_ACTION.value is too long');
        return ok({ action: action, variantId: payload['variantId'], value, v: v });
    }
    if (action === 'SET_GAME') {
        if (!hasOnlyKeys(payload, ['action', 'gameId', 'v']))
            return fail('HOST_ACTION.SET_GAME has unexpected keys');
        // Shape only. WHICH games exist, and which this host owns, is the
        // adapter's business - the protocol has no catalogue.
        if (!isPhraseId(payload['gameId'], Limits.MOVE_NAME))
            return fail('HOST_ACTION.gameId is not a valid id');
        return ok({ action: action, gameId: payload['gameId'], v: v });
    }
    if (action === 'REMOVE_OBSERVER' || action === 'MAKE_PLAYER') {
        if (!hasOnlyKeys(payload, ['action', 'observerId', 'v']))
            return fail('HOST_ACTION.' + action + ' has unexpected keys');
        if (!isNonNegativeInt(payload['observerId']))
            return fail('HOST_ACTION.observerId must be a non-negative integer');
        return ok({ action: action, observerId: payload['observerId'], v: v });
    }
    return fail('unknown host action: ' + action);
}
//# sourceMappingURL=validate.js.map